import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { addCompletedBatch } from '../store/slices/todoSlice';
import { v4 as uuidv4 } from 'uuid';

interface AddFillingInventoryModalProps {
  onClose: () => void;
}

export const AddFillingInventoryModal: React.FC<AddFillingInventoryModalProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const fillings = useAppSelector(state => state.recipes.fillings);

  const [quantities, setQuantities] = useState<{ [recipeId: string]: string }>({});

  const handleQuantityChange = (recipeId: string, value: string) => {
    setQuantities(prev => ({
      ...prev,
      [recipeId]: value
    }));
  };

  const handleAddToInventory = () => {
    const now = new Date().toISOString();

    Object.entries(quantities).forEach(([recipeId, quantityStr]) => {
      const quantity = parseInt(quantityStr, 10);
      if (quantity && quantity > 0) {
        const filling = fillings.find(f => f.id === recipeId);
        if (filling) {
          dispatch(
            addCompletedBatch({
              id: uuidv4(),
              fillingName: filling.name,
              batchCount: quantity,
              completedAt: now,
              taskId: 'manual-entry'
            })
          );
        }
      }
    });

    onClose();
  };

  const hasValidQuantities = Object.values(quantities).some(q => {
    const num = parseInt(q, 10);
    return num && num > 0;
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>Add Filling Inventory</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px' }}>
          {fillings.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              No filling recipes available. Create recipes on the Recipes page first.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {fillings.map(filling => (
                <div
                  key={filling.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <label
                    htmlFor={`quantity-${filling.id}`}
                    style={{
                      fontWeight: '500',
                      color: '#333',
                      fontSize: '15px',
                      flex: 1
                    }}
                  >
                    {filling.name}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      id={`quantity-${filling.id}`}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={quantities[filling.id] || ''}
                      onChange={e => handleQuantityChange(filling.id, e.target.value)}
                      style={{
                        width: '100px',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                        textAlign: 'right'
                      }}
                    />
                    <span style={{ color: '#666', fontSize: '14px', minWidth: '60px' }}>
                      batch{quantities[filling.id] && parseInt(quantities[filling.id]) !== 1 ? 'es' : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              backgroundColor: 'white',
              color: '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
          >
            Cancel
          </button>
          <button
            onClick={handleAddToInventory}
            disabled={!hasValidQuantities}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: hasValidQuantities ? '#4CAF50' : '#ccc',
              color: 'white',
              cursor: hasValidQuantities ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s',
              boxShadow: hasValidQuantities ? '0 2px 4px rgba(76, 175, 80, 0.3)' : 'none'
            }}
            onMouseEnter={e => {
              if (hasValidQuantities) {
                e.currentTarget.style.backgroundColor = '#45a049';
              }
            }}
            onMouseLeave={e => {
              if (hasValidQuantities) {
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }
            }}
          >
            Add to Inventory
          </button>
        </div>
      </div>
    </div>
  );
};
